import React, { useEffect, useRef, useState } from "react";
import { UiComponents, UnoStoreHooks } from "@simulanisindia/ui";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { debounce, VALIDATE_REGEX, isValidFile, modalName, sanitizeWithRemoveSpace } from "../utils/general";
import { defaultLogo, UploadIcon } from "../Icons";
import { CREATE_GROUP, GROUP_UPDATE, GROUP_DETAILS, UPLOAD_GROUP_LOGO, CREATE_TAGS } from "../apiClient/apiEndpoints";
import apiClient from "../apiClient";
import { getGroupHierarchy, getGroupChildrenList } from "../apiClient/apiDataFetcher";
import { useModalContext } from "./context/ModalProvider";
import BtnLoad from "../components/BtnLoad";
import { ACCEPTED_IMAGE_TYPES } from "../constant/constants";
import DropdownWithCreation from "../components/DropdownWithCreation";
import { v4 as uuidv4 } from "uuid";

const { SimInputBar, SimToggleSwitch, DropdownSearch } = UiComponents;
const { useUnoSelector } = UnoStoreHooks;

const GroupAddData = ({ isMobileView }) => {
  const dispatch = useDispatch();
  const configData = useUnoSelector((state) => state.config);
  const userData = useUnoSelector((state) => state.user);
  const configSlice = useSelector((state) => state.configSlice);
  const fileInputRef = useRef(null);
  const { modalState, openModal, closeModal, handleLoading } = useModalContext();
  const { params, id, type, loading, handleRefreshTab } = modalState;
  const { uuid } = params;
  const [groupeTypes, setGroupeTypes] = useState([]);
  const [error, setError] = useState({
    name: null,
    identifier: null,
    groupType: null,
    file: null,
    tags: null,
    userRoleType: null,
  });

  const [groupDetails, setGroupDetails] = useState({
    groupName: "",
    identifier: "",
    active: true,
    groupType: null,
    logo: null,
    handDown: true,
    tags: null,
    userRoleType: null,
  });
  const [groupTags, setGroupTags] = useState([]);
  const [roleTypes, setRoleTypes] = useState([]);

  useEffect(() => {
    let typeList = configSlice.groupTypes.map((role) => {
      return { value: role.id, label: role.name };
    });
    setGroupeTypes(typeList);
    let roleTypeList = configSlice.roles.map((role) => {
      return { value: role.id, label: role.name };
    });
    setRoleTypes(roleTypeList);
  }, [configSlice]);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getDetails = async (id) => {
    try {
      let data = await apiClient.get(`${configData.config.UNO_URL}${GROUP_DETAILS}/${id}`);
      let findRole = groupeTypes.filter((e) => e.value === data?.type?.id)[0] || null;
      let groupTags = data?.data?.tags?.map((item) => ({ label: item, value: uuidv4() }));
      let defaultUserRoleType = roleTypes.find((item) => item.value == data?.data?.userRoleType);

      setGroupDetails(() => ({
        groupName: data.name,
        identifier: data.identifier,
        active: data.active,
        logo: data.avatar,
        groupType: findRole,
        tags: groupTags,
        userRoleType: defaultUserRoleType,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (type === "update" && groupeTypes?.length && roleTypes?.length) {
      getDetails(id);
    }
  }, [modalState, groupeTypes, roleTypes]);

  const handleLogoUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (isValidFile(file, ACCEPTED_IMAGE_TYPES)) {
        setError((prev) => ({ ...prev, file: null }));
      } else {
        setError((prev) => ({ ...prev, file: "Invalid Image" }));
        return;
      }
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axios.post(`${configData.config.UNO_URL}${UPLOAD_GROUP_LOGO}/${uuid}`, formData, {
        headers: {
          Authorization: `Bearer ${userData?.token}`,
          "Content-Type ": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          // let uploadProgress = Math.floor(progress);
          // uploadProgress(file, Math.floor(progress));
        },
      });

      setGroupDetails((pre) => ({ ...pre, ["logo"]: data.url }));
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancelModal = async () => {
    setGroupDetails({
      groupName: "",
      identifier: "",
      active: true,
      groupType: null,
      logo: null,
    });
    closeModal();
  };

  const checkValidation = () => {
    const errors = {};

    const validations = [
      { field: "name", condition: groupDetails.groupName.trim().length === 0, message: "Kindly fill name." },
      { field: "groupType", condition: !groupDetails.groupType, message: "Kindly select group type." }
    ];

    validations.forEach(({ field, condition, message }) => {
      if (condition) errors[field] = message;
    });

    setError((prev) => ({ ...prev, ...errors }));

    return Object.keys(errors).length > 0;
  };

  const handleGroupCreate = async () => {
    setError({
      name: null,
      identifier: null,
      groupType: null,
    });
    try {
      if (checkValidation()) return;
      handleLoading(true);
      let bodyData = {
        group: {
          name: groupDetails.groupName,
          identifier: groupDetails.identifier,
          avatar: groupDetails.logo,
          active: groupDetails.active,
          handDown: groupDetails.handDown,
          typeId: groupDetails.groupType.value,
          tags: groupDetails?.tags?.map((e) => e.label) || [],
          userRoleType: groupDetails?.userRoleType?.value || null,
        },
      };

      await apiClient.post(`${configData.config.UNO_URL}${CREATE_GROUP}/${uuid}`, bodyData);

      if (handleRefreshTab) handleRefreshTab();
      getGroupHierarchy(dispatch, configData.config.UNO_URL, userData.user.group.uuid);
      getGroupChildrenList(dispatch, configData.config.UNO_URL, userData.user.group.uuid);
      handleCancelModal();
    } catch (error) {
      console.log(error);
    } finally {
      handleLoading(false);
    }
  };

  const handleGroupUpdate = async () => {
    setError({
      name: null,
      identifier: null,
      groupType: null,
    });
    try {
      if (checkValidation()) return;
      handleLoading(true);
      let bodyData = {
        name: groupDetails.groupName,
        identifier: groupDetails.identifier,
        avatar: groupDetails.logo,
        active: groupDetails.active,
        groupType: groupDetails.groupType.value,
        tags: groupDetails.tags?.map((e) => e.label) || [],
        userRoleType: groupDetails?.userRoleType?.value || null,
      };

      await apiClient.post(`${configData.config.UNO_URL}${GROUP_UPDATE}/${id}`, bodyData);
      closeModal();
      handleCancelModal();
      if (handleRefreshTab) handleRefreshTab();
    } catch (error) {
      console.log(error);
    } finally {
      handleLoading(false);
    }
  };

  const handleOpenModal = () => {
    const { handleRefreshTab, params } = modalState;
    openModal({ modalName: modalName.GROUP_CSV, handleRefreshTab, params });
  };

  const handleFormGroupCreate = debounce(handleGroupCreate, 500);
  const handleFormGroupUpdate = debounce(handleGroupUpdate, 500);

  const addNewGroupTag = async (newLabel) => {
    try {
      if (groupTags.find((item) => item.label == newLabel)) return;
      const newTag = { value: uuidv4(), label: newLabel };
      setGroupTags((prev) => [...prev, newTag]);
      setGroupDetails((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), newTag],
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateGroupDetails = (name, value, shouldValidate) => {
    if(shouldValidate && VALIDATE_REGEX.test(value)) return;
    setGroupDetails((pre) => ({ ...pre, [name]: value }))
  }

  return (
    <>
      <div className="user-add">
        <div className="profile-n-stuff">
          <div className="profile-view">
            <img src={groupDetails.logo || defaultLogo} referrerPolicy={"no-referrer"} />
          </div>
          <div className="action-btn">
            <button className="gradient-btn" onClick={handleButtonClick}>
              <UploadIcon /> {type === "create" ? "Upload" : "Update"} Profile
              <input
                type="file"
                ref={fileInputRef}
                className="displayNone"
                onChange={handleLogoUpload}
                accept={ACCEPTED_IMAGE_TYPES.join(", ")}
              />
            </button>
            {error.file && <p>{error.file}</p>}
          </div>
        </div>

        <div className="container-fluid my-4 px-0 h-100">
          <div className="row group-add-data">
            <div className="col-md-6 h-100">
              <SimInputBar
                label="Group Name *"
                errorMessage={error.name}
                type="text"
                placeholder="Group Name"
                name="groupName"
                value={groupDetails.groupName}
                onChange={(e) => handleUpdateGroupDetails(e.target.name, e.target.value, true)}
                />
            </div>

            <div className="col-md-6 h-100">
              <SimInputBar
                label="Group Identifier"
                errorMessage={error.identifier}
                placeholder="Group Identifier"
                value={groupDetails.identifier}
                name="identifier"
                onChange={(e) => handleUpdateGroupDetails(e.target.name, sanitizeWithRemoveSpace(e.target.value), true)}
              />
            </div>

            <div className="col-md-12 h-100">
              <DropdownSearch
                label="Group Type *"
                error={error.groupType}
                selectProps={{
                  options: groupeTypes,
                  value: groupDetails.groupType,
                  isDisabled: id === userData.user.group.uuid,
                  onChange: (e) => setGroupDetails((pre) => ({ ...pre, ["groupType"]: e })),
                }}
              />
            </div>
            <div className="col-md-12 h-100">
              <DropdownWithCreation
                label="Group Tags"
                error={error.tags}
                selectProps={{
                  options: groupTags,
                  value: groupDetails.tags,
                  isDisabled: id === userData.user.group.uuid,
                  onChange: (e) =>
                    setGroupDetails((pre) => ({
                      ...pre,
                      tags: e,
                    })),
                  isMulti: true,
                  func: addNewGroupTag,
                  placeholder: "Add Tags...",
                }}
              />
            </div>
            <div className="col-md-12 h-100">
              <DropdownSearch
                label="Default User Role Type"
                error={error.userRoleType}
                selectProps={{
                  options: roleTypes,
                  value: groupDetails.userRoleType,
                  isDisabled: id === userData.user.group.uuid,
                  onChange: (e) => setGroupDetails((pre) => ({ ...pre, ["userRoleType"]: e })),
                }}
              />
            </div>

            {/* <div className="col-md-6 h-100">
              <DropdownSearch
                label="Country/Region"
                selectProps={{
                  options: [
                    { value: "chocolate", label: "Chocolate" },
                    { value: "strawberry", label: "Strawberry" },
                    { value: "vanilla", label: "Vanilla" },
                  ],
                }}
              />
            </div>

            <div className="col-md-6 h-100">
              <DropdownSearch
                label="City/State"
                selectProps={{
                  options: [
                    { value: "chocolate", label: "Chocolate" },
                    { value: "strawberry", label: "Strawberry" },
                    { value: "vanilla", label: "Vanilla" },
                  ],
                }}
              />
            </div> */}

            <div className="col-md-6">
              <SimToggleSwitch
                label="Status"
                checked={groupDetails.active}
                onChange={(e) => setGroupDetails((pre) => ({ ...pre, ["active"]: !pre.active }))}
                toggleKeyNames="Active"
                ToggleKeyClass="active-inactive"
                disabled={id === userData.user.group.uuid}
              />
            </div>
            {/* 
            <div className="col-md-6">
              <SimToggleSwitch
                label="Unristricted"
                checked={groupDetails.handDown}
                onChange={(e) => setGroupDetails((pre) => ({ ...pre, ["handDown"]: !pre.handDown }))}
                toggleKeyNames="Active"
                ToggleKeyClass="active-inactive"
                disabled={id === userData.user.group.uuid}
              />
            </div> */}

            {/* {type === "create" &&
              <div className="col-md-12 ">
                <div className="add-admin">
                  <p>Admin</p>
                  <div className="admin-list">
                    <span className="add-admin">
                      <AddIcon />
                    </span>
                    <span>
                      <img src="https://cdn.simulanis.io/sso/uno/development/teams/a980ec6b-959e-4d68-83cc-4ca888f7f936/avatar/0d89224d-5ee9-42e3-b749-dbf1a11760dd.jpg" />
                    </span>
                    <span>
                      <img src="https://cdn.simulanis.io/sso/uno/development/teams/a980ec6b-959e-4d68-83cc-4ca888f7f936/avatar/0d89224d-5ee9-42e3-b749-dbf1a11760dd.jpg" />
                    </span>
                    <span>
                      <img src="https://cdn.simulanis.io/sso/uno/development/teams/a980ec6b-959e-4d68-83cc-4ca888f7f936/avatar/0d89224d-5ee9-42e3-b749-dbf1a11760dd.jpg" />
                    </span>
                    <span>
                      <img src="https://cdn.simulanis.io/sso/uno/development/teams/a980ec6b-959e-4d68-83cc-4ca888f7f936/avatar/0d89224d-5ee9-42e3-b749-dbf1a11760dd.jpg" />
                    </span>
                    <p>+ 4 more</p>
                  </div>
                </div>
              </div>} */}
          </div>
        </div>
      </div>

      <div className="modal-footer">
        {!isMobileView && type === "create" && (
          <p className="link-text" onClick={handleOpenModal}>
            Group Upload CSV
          </p>
        )}

        <div className="modal-footer-btns">
          <button className="cancel" onClick={handleCancelModal}>
            Cancel
          </button>

          {type === "create" ? (
            <button className="save" onClick={handleFormGroupCreate}>
              {loading ? <BtnLoad /> : "Add"}
            </button>
          ) : (
            <button className="save" onClick={handleFormGroupUpdate}>
              {loading ? <BtnLoad /> : "Update"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default GroupAddData;
